import { useCallback, useState } from 'react'
import {
  clients,
  clientsDetails,
  clientsBillingAddress,
  clientsShippingAddress,
  clientsConfirmEmail,
  updateClientsDetails,
  updateClientsBillingAddress,
  updateClientsShippingAddress,
  createClients,
  resetRedeemCounter,
  troubleshootUser,
  type IClient,
  type PaginationParams,
  type IClientPayload,
  type TroubleshootUserParams,
} from '@/core/services/clients'
import type { CreatClient } from '@/common/models/clients'

export const useClients = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchClients = useCallback(async (params: PaginationParams) => {
    setLoading(true)
    setError(null)
    try {
      const { data, status } = await clients(params)
      if (status === 200) {
        return data
      }
      throw new Error('Failed to fetch clients')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchClientDetails = useCallback(async (clientId: string) => {
    setLoading(true)
    setError(null)
    try {
      const { data, status } = await clientsDetails(clientId)
      if (status === 200) {
        return data
      }
      throw new Error('Failed to fetch client details')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchClientAddresses = useCallback(async (clientId: string) => {
    setError(null)
    try {
      const [shipping, billing] = await Promise.all([
        clientsShippingAddress(clientId),
        clientsBillingAddress(clientId),
      ])
      return { shipping: shipping.data, billing: billing.data }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    }
  }, [])

  const createClient = useCallback(async (data: CreatClient) => {
    setLoading(true)
    setError(null)
    try {
      const response = await createClients(data)
      if (response.status === 200) {
        return response.data
      }
      throw new Error('Failed to create client')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const updateClientDetails = useCallback(
    async (clientId: string, payload: IClientPayload) => {
      setLoading(true)
      setError(null)
      try {
        const resp = await updateClientsDetails(clientId, payload)
        if (resp.status === 200) {
          return resp.data
        }
        throw new Error('Failed to update client details')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        setError(message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const updateBillingAddress = useCallback(
    async (
      clientId: string,
      data: {
        id: string
        address: string
        country: string
        city: string
        postCode: string
        nationality: string
      },
    ) => {
      setLoading(true)
      setError(null)
      try {
        const resp = await updateClientsBillingAddress(clientId, data)
        if (resp.status === 200) {
          return resp.data
        }
        throw new Error('Failed to update billing address')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        setError(message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const updateShippingAddress = useCallback(
    async (
      clientId: string,
      data: {
        id: string
        address: string
        country: string
        city: string
        postCode: string
        useDataFromBillingAddress: boolean
      },
    ) => {
      setLoading(true)
      setError(null)
      try {
        const resp = await updateClientsShippingAddress(clientId, data)
        if (resp.status === 200) {
          return resp.data
        }
        throw new Error('Failed to update shipping address')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        setError(message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const sendConfirmationEmail = useCallback(
    async (clientId: string, email: string) => {
      setLoading(true)
      setError(null)
      try {
        const resp = await clientsConfirmEmail(clientId, { email })
        if (resp.status === 200) {
          return resp.data
        }
        throw new Error('Failed to send confirmation email')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        setError(message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const resetRedeem = useCallback(async (clientId: string) => {
    setLoading(true)
    setError(null)
    try {
      const resp = await resetRedeemCounter(clientId)
      if (resp.status === 200) {
        return resp.data
      }
      throw new Error('Failed to reset redeem counter')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const troubleshoot = useCallback(async (params: TroubleshootUserParams) => {
    setLoading(true)
    setError(null)
    try {
      const { data, status } = await troubleshootUser(params)
      if (status === 200) {
        return data
      }
      throw new Error('Failed to troubleshoot user')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    fetchClients,
    fetchClientDetails,
    fetchClientAddresses,
    createClient,
    updateClientDetails,
    updateBillingAddress,
    updateShippingAddress,
    sendConfirmationEmail,
    resetRedeem,
    troubleshoot,
  }
}
